import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import { BookmarkCheck, History, LockKeyhole, Sparkles, TrendingUp } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { ForgotPasswordForm, GoogleSignInButton, SigninForm, SignupForm } from "@lyder-talk/ui";
import { useToast } from "@/hooks/use-toast";
import { getAppUrl, signInWithGoogle } from "@lyder-talk/core";
import { sendDiscordWebhook } from "@/lib/discord-webhook";
import { primaryCtaClass } from "@/lib/card-styles";
import {
  useForgotPasswordFormCopy,
  useSigninFormCopy,
  useSignupFormCopy,
} from "./auth-form-copy";
import { auth } from "@lyder-talk/core/lib/auth-client";

/**
 * One line of "what an account gets you". The icon is passed in rather than
 * named by string so call sites stay type-checked, and the text is already
 * translated by whoever knows the reason the modal opened.
 */
export interface AuthRequiredBenefit {
  icon: LucideIcon;
  text: string;
}

type AuthView = "gate" | "signin" | "signup" | "forgot";

export interface AuthRequiredModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The gate's headline. Falls back to a generic "sign in to continue". */
  title?: string;
  /** The gate's one-line reason. Falls back to a generic promise of what an account unlocks. */
  description?: string;
  /** Replaces the default four-item benefit list. */
  benefits?: AuthRequiredBenefit[];
  /**
   * Which screen to open on, defaulting to the gate.
   *
   * The gate is right for a *blocked action* — the visitor did not ask to sign
   * in, so it has to explain itself first. A caller whose visitor already chose
   * (a "Sign in" or "Create account" button) should pass `signin` / `signup`:
   * the reason screen would be a detour in front of a decision they have made.
   */
  initialStage?: AuthView;
  /**
   * Fired once sign-in / sign-up has produced a session, *before* the modal
   * closes. The provider uses it to resume whatever the learner was doing.
   */
  onAuthed?: () => void;
}

/**
 * The sign-in gate that stands in front of an action an anonymous visitor
 * cannot take.
 *
 * It is deliberately **not** a redirect. The old flow bounced the visitor to
 * `/auth/signin?redirectTo=…`, which lost the sentence they had just typed, the
 * recording they had just made, and the page they were reading — and on a
 * shared session link it dropped them out of someone else's topic entirely.
 * The modal keeps the page intact behind it and answers the only question that
 * matters ("why do you want my account?") in the visitor's own context.
 *
 * Shape: gate (reason + benefits + one primary action, with Google and
 * create-account as the quiet alternatives) → the *shared* sign-in / sign-up
 * forms from `@lyder-talk/ui`, the same ones `/auth/*` renders. The gate is the
 * only bespoke surface here; the forms are not re-implemented, so the modal and
 * the standalone pages can never disagree about how signing in works.
 *
 * Google is offered on the gate *as well as* inside the forms on purpose: most
 * gated visitors want the one-tap path, and making them open a form to find it
 * is a step for nothing. It is the one flow here that leaves the page, so it
 * carries the current path with it — see `handleGoogle`.
 */
export const AuthRequiredModal = ({
  open,
  onOpenChange,
  title,
  description,
  benefits,
  initialStage = "gate",
  onAuthed,
}: AuthRequiredModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const signinCopy = useSigninFormCopy();
  const signupCopy = useSignupFormCopy();
  const forgotCopy = useForgotPasswordFormCopy();

  const [view, setView] = useState<AuthView>(initialStage);
  const [googleLoading, setGoogleLoading] = useState(false);

  // A fresh reason deserves a fresh gate. Without this, reopening the modal
  // after a previous attempt would land the visitor on a half-filled sign-up
  // form for an action they had long since abandoned.
  useEffect(() => {
    if (open) setView(initialStage);
  }, [open, initialStage]);

  const defaultBenefits: AuthRequiredBenefit[] = [
    {
      icon: BookmarkCheck,
      text: t("auth.modal.required.benefits.save", "Save and reopen your recordings"),
    },
    {
      icon: Sparkles,
      text: t("auth.modal.required.benefits.feedback", "Get AI feedback on your speaking"),
    },
    {
      icon: TrendingUp,
      text: t("auth.modal.required.benefits.progress", "Track your progress across sessions"),
    },
    {
      icon: History,
      text: t("auth.modal.required.benefits.history", "Keep a history you can revisit"),
    },
  ];

  const benefitList = benefits ?? defaultBenefits;

  const handleSignIn = async (email: string, password: string) => {
    const { error } = await auth.signInWithPassword({ email, password });
    if (error) {
      toast({
        title: t("auth.modal.toasts.error", "Error"),
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
    toast({ title: t("auth.forms.toasts.welcomeBack", "Welcome back!") });
    onAuthed?.();
  };

  const handleSignUp = async (email: string, password: string, displayName: string) => {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      const { data, error } = await auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          emailRedirectTo: getAppUrl("/auth/confirm"),
          data: { display_name: displayName.trim() || undefined },
        },
      });

      if (error) {
        const alreadyExists = /already registered|already exists/i.test(error.message);
        if (alreadyExists) {
          toast({
            title: t("auth.forms.toasts.accountExists", "Account already exists"),
            description: t(
              "auth.forms.toasts.accountExistsDescription",
              "You already have an account! Please sign in.",
            ),
            variant: "destructive",
          });
          return {
            success: false,
            alreadyExists: true,
            error: t(
              "auth.forms.toasts.accountExistsDescription",
              "You already have an account! Please sign in.",
            ),
          };
        }
        throw error;
      }

      void sendDiscordWebhook({
        eventType: "user_signup",
        email: normalizedEmail,
        timestamp: new Date().toISOString(),
      });

      if (data?.session) {
        // Signed in immediately (email confirmation off) — resume the action.
        toast({ title: t("auth.forms.toasts.welcome", "Welcome!") });
        onAuthed?.();
      } else {
        // Confirmation required. The form's own success panel is the next
        // step, and the gate stays closed on the action until they come back.
        toast({
          title: t("auth.modal.toasts.checkEmail", "Check your email"),
          description: t(
            "auth.modal.toasts.checkEmailDescription",
            "Please confirm your email to activate your account.",
          ),
        });
      }

      return { success: true, requiresEmailConfirmation: !data?.session };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t("common.errors.generic", "Something went wrong.");
      toast({
        title: t("auth.forms.toasts.error", "Error"),
        description: message,
        variant: "destructive",
      });
      return { success: false, error: message };
    }
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      // Google leaves the page, so the pending request cannot be resumed — but
      // the visitor can at least be returned to the page they were blocked on
      // instead of the home page, mid-task.
      await signInWithGoogle(`${window.location.pathname}${window.location.search}`);
    } catch {
      toast({
        title: t("auth.forms.toasts.error", "Error"),
        description: t("auth.forms.toasts.googleFailed", "Failed to sign in with Google"),
        variant: "destructive",
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = async (email: string) => {
    const { error } = await auth.resetPasswordForEmail(email, {
      redirectTo: getAppUrl("/auth/reset-password"),
    });
    if (error) throw new Error(error.message);
  };

  /*
   * The shared forms bring their own heading (logo, serif title), so the modal
   * steps out of the way once we leave the gate: no competing title, just an
   * `sr-only` one for the dialog's accessible name. It names *why* the dialog
   * is open rather than repeating the form's own `<h1>` — a screen reader would
   * otherwise announce "Welcome back" twice.
   */
  const accessibleTitle = title ?? t("auth.modal.required.title", "Sign in to continue");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {view === "gate" ? (
        <DialogContent
          size="md"
          className="overflow-hidden p-0 sm:max-w-[26rem]"
          data-testid="auth-required-modal"
        >
          <div className="px-6 pb-6 pt-7">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/[0.08] text-primary dark:bg-primary/[0.12]">
              <LockKeyhole className="h-5 w-5" aria-hidden="true" />
            </div>

            <DialogTitle className="font-display text-[22px] font-semibold leading-tight tracking-tight text-stone-900 dark:text-stone-50">
              {title ?? t("auth.modal.required.title", "Sign in to continue")}
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-relaxed text-stone-500 dark:text-stone-400">
              {description ??
                t(
                  "auth.modal.required.description",
                  "Create a free account to save your work and pick up right where you left off.",
                )}
            </DialogDescription>

            <div className="mt-5 space-y-0.5 rounded-2xl border border-stone-200/70 bg-stone-50/70 p-2 dark:border-stone-800 dark:bg-stone-800/30">
              {benefitList.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2.5 rounded-xl px-2 py-1.5">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-white text-primary shadow-sm dark:bg-stone-900">
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                  <span className="text-[13px] leading-snug text-stone-600 dark:text-stone-300">
                    {text}
                  </span>
                </div>
              ))}
            </div>

            {/*
              One primary action, and the two ways in demoted to a quiet button
              and a link.

              Google sits directly under the primary because it is a *sign-in
              method*, a peer of the email form — it belongs in the same block.
              "Create a free account" is a different flow, so it steps down to a
              link. Three stacked full-width buttons read as a wall and all
              shout at the same volume; this keeps the gate at one voice.
            */}
            <div className="mt-5 space-y-2">
              <button
                type="button"
                onClick={() => setView("signin")}
                className={primaryCtaClass}
              >
                {t("auth.modal.required.signIn", "Sign in")}
              </button>
              <GoogleSignInButton
                onClick={handleGoogle}
                loading={googleLoading}
                className="h-10 rounded-lg text-sm font-semibold"
                label={t("auth.forms.signin.continueWithGoogle", "Continue with Google")}
              />
            </div>

            <p className="mt-4 text-center text-[13px] text-stone-500 dark:text-stone-400">
              <button
                type="button"
                onClick={() => setView("signup")}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                {t("auth.modal.required.signUp", "Create a free account")}
              </button>
            </p>

            <p className="mt-3 text-center text-[11px] font-semibold uppercase tracking-wider text-stone-500 dark:text-stone-400">
              {t("auth.modal.required.footnote", "Free to start · no card required")}
            </p>
          </div>
        </DialogContent>
      ) : (
        <DialogContent
          size="md"
          className="max-h-[92vh] overflow-y-auto px-6 pb-7 pt-8 sm:max-w-[26rem]"
          data-testid="auth-required-modal"
        >
          <DialogTitle className="sr-only">{accessibleTitle}</DialogTitle>
          <DialogDescription className="sr-only">
            {t(
              "auth.modal.required.formDescription",
              "Sign in or create an account to continue.",
            )}
          </DialogDescription>

          {view === "signin" && (
            <SigninForm
              onSubmit={handleSignIn}
              onNavigateToSignup={() => setView("signup")}
              onNavigateToForgotPassword={() => setView("forgot")}
              onGoogleSignIn={handleGoogle}
              googleLoading={googleLoading}
              copy={signinCopy}
            />
          )}

          {view === "signup" && (
            <SignupForm
              onSubmit={handleSignUp}
              onNavigateToSignin={() => setView("signin")}
              onGoogleSignIn={handleGoogle}
              googleLoading={googleLoading}
              copy={signupCopy}
            />
          )}

          {view === "forgot" && (
            <ForgotPasswordForm
              onSubmit={handleForgotPassword}
              onNavigateToSignin={() => setView("signin")}
              copy={forgotCopy}
            />
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};

export default AuthRequiredModal;
